import BaseModel from '@core/model';

import RatePlanCalculationTypeEnum from '@constants/enums/ratePlan/calculationType';
import RatePlanStatusEnum from '@constants/enums/ratePlan/status';
import RatePlanSurchargeApplicationEnum from '@constants/enums/ratePlan/surchargeApplication';
import RatePlanTypeEnum from '@constants/enums/ratePlan/type';

import RatePlanApi from '@packages/rate-plans/api';

import i18n from '@plugins/i18n';

import Adviser from '@/models/Adviser';

export class RatePlan extends BaseModel {
    static name = 'RatePlan';
    static entity = 'ratePlans';
    static Api = RatePlanApi;

    static fields() {
        return {
            ...super.fields(),
            id: this.attr(null),
            name: this.string(null).nullable(),
            description: this.string(null).nullable(),
            status: this.enum(RatePlanStatusEnum).nullable(),
            type: this.enum(RatePlanTypeEnum).nullable(),
            default: this.boolean(false),
            adviser_id: this.string(null).nullable(),
            adviser: this.belongsTo(Adviser, 'adviser_id'),
            minimum_fee: this.attr(this.defaultMoney).nullable(),
            maximum_fee: this.attr(this.defaultMoney).nullable(),
            calculation_type: this.enum(RatePlanCalculationTypeEnum).nullable(),
            minimum_aua_threshold: this.attr(this.defaultMoney).nullable(),
            surcharge: this.attr(this.defaultMoney).nullable(),
            surcharge_label: this.string(null).nullable(),
            surcharge_application: this.enum(RatePlanSurchargeApplicationEnum).nullable()
        };
    }

    get status_label() {
        return this.status ? i18n.t(`enums.rate_plan_status.${this.status}`) : null;
    }

    get type_label() {
        return this.type ? i18n.t(`enums.rate_plan_type.${this.type}`) : null;
    }

    get calculation_type_label() {
        return this.calculation_type ? i18n.t(`enums.rate_plan_calculation_type.${this.calculation_type}`) : null;
    }

    get surcharge_application_label() {
        return this.surcharge_application
            ? i18n.t(`enums.rate_plan_surcharge_application.${this.surcharge_application}`)
            : null;
    }

    get is_active() {
        return this.status === RatePlanStatusEnum.ACTIVE;
    }

    get is_default() {
        return Boolean(this.default);
    }

    static mock(overrides = {}) {
        return {
            id: faker => faker.string.uuid(),
            name: faker => faker.commerce.productName(),
            description: faker => faker.lorem.sentence(),
            status: faker => faker.helpers.arrayElement(Object.values(RatePlanStatusEnum)),
            type: faker => faker.helpers.arrayElement(Object.values(RatePlanTypeEnum)),
            default: faker => faker.datatype.boolean(0.2),
            adviser_id: faker => overrides.adviser_id || faker.string.uuid(),
            minimum_fee: faker => ({ amount: faker.number.int({ min: 0, max: 500 }), currency: 'GBP' }),
            maximum_fee: () => null,
            calculation_type: faker => faker.helpers.arrayElement(Object.values(RatePlanCalculationTypeEnum)),
            minimum_aua_threshold: () => null,
            surcharge: () => null,
            surcharge_label: () => null,
            surcharge_application: () => null
        };
    }
}

export default RatePlan;
